import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Alert } from '../components/ui/alert';
import { resetPassword } from '../services/auth.service';

const resetPasswordSchema = z.object({
  newPassword: z
    .string()
    .min(8, 'min8')
    .regex(/[A-Z]/, 'requirements')
    .regex(/[a-z]/, 'requirements')
    .regex(/[0-9]/, 'requirements')
    .regex(/[!@#$%^&*(),.?":{}|<>]/, 'requirements'),
  confirmPassword: z.string().min(1, 'required'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'mismatch',
  path: ['confirmPassword'],
});

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      setError(t('pages.resetPassword.errors.missingToken'));
      return;
    }

    setError('');
    setSuccessMessage('');
    setIsLoading(true);

    try {
      const response = await resetPassword(token, data.newPassword);
      setSuccessMessage(response.message || t('pages.resetPassword.success'));
      setTimeout(() => navigate('/login'), 1200);
    } catch {
      setError(t('pages.resetPassword.errors.submit'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900">
            <ShieldCheck className="h-6 w-6 text-orange-600 dark:text-orange-400" />
          </div>
          <CardTitle className="text-2xl font-bold">{t('pages.resetPassword.title')}</CardTitle>
          <CardDescription>{t('pages.resetPassword.subtitle')}</CardDescription>
        </CardHeader>

        <form onSubmit={handleFormSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            {!token && <Alert variant="error">{t('pages.resetPassword.errors.missingToken')}</Alert>}
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            {error && <Alert variant="error">{error}</Alert>}

            <div className="space-y-2">
              <Label htmlFor="newPassword">{t('pages.resetPassword.newPassword')}</Label>
              <Input id="newPassword" type="password" autoComplete="new-password" {...register('newPassword')} disabled={isLoading || !token} />
              {errors.newPassword && <p className="text-sm text-red-500">{t('pages.resetPassword.errors.requirements')}</p>}
              <p className="text-xs text-muted-foreground">{t('common.passwordRequirements')}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('pages.resetPassword.confirmPassword')}</Label>
              <Input id="confirmPassword" type="password" autoComplete="new-password" {...register('confirmPassword')} disabled={isLoading || !token} />
              {errors.confirmPassword && (
                <p className="text-sm text-red-500">
                  {errors.confirmPassword.message === 'mismatch'
                    ? t('pages.resetPassword.errors.mismatch')
                    : t('pages.resetPassword.errors.confirmRequired')}
                </p>
              )}
            </div>
          </CardContent>

          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" disabled={isLoading || !token}>
              {isLoading ? t('common.loading') : t('pages.resetPassword.submit')}
            </Button>
            <Link to="/login" className="text-sm text-orange-600 hover:underline">
              {t('pages.resetPassword.backToLogin')}
            </Link>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default ResetPassword;
